import { CommonModule, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  GouvDvfDataDto,
  PropertyDto,
  PropertyId,
  PropertyLocationDto,
  PropertyType,
  UserApplications,
} from '@workspace/data-types';
import { ApplicationStore, UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { catchError, finalize, of, tap } from 'rxjs';
import { PropertyDvfGaugeComponent } from '../property-dvf-gauge/property-dvf-gauge.component';

@Component({
  selector: 'etxe-property-dvf-market-reference',
  standalone: true,
  imports: [
    CommonModule,
    DecimalPipe,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    PropertyDvfGaugeComponent,
  ],
  templateUrl: './property-dvf-market-reference.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyDvfMarketReferenceComponent {
  private apiService = inject(ApiService);
  private destroyRef = inject(DestroyRef);
  private readonly appStore = inject(ApplicationStore);
  private readonly userStore = inject(UserStore);

  readonly isAdminApp = computed(() => this.appStore.name() === UserApplications.ADMIN);

  property = input<PropertyDto | undefined>();
  propertyLocation = input<PropertyLocationDto | undefined>();
  propertyType = input<PropertyType | undefined>();
  habitableSurface = input<number | undefined>();
  propertyDvf = input<GouvDvfDataDto | undefined>();
  propertyId = input<PropertyId | undefined>();
  currentPrice = input<number | null | undefined>();
  showGauge = input<boolean>(true);

  private fetchedDvfData = signal<GouvDvfDataDto | null>(null);
  isDvfLoading = signal<boolean>(false);
  dvfError = signal<string | null>(null);
  private lastFetchedDvfKey: string | null = null;

  readonly effectiveSurface = computed<number | null>(() => {
    return this.habitableSurface() ?? this.property()?.keyInfo?.surface?.habitable ?? null;
  });

  readonly effectiveLocation = computed<PropertyLocationDto | null>(() => {
    return this.propertyLocation() ?? this.property()?.location ?? null;
  });

  readonly effectivePropertyType = computed<PropertyType>(() => {
    return this.propertyType() ?? this.property()?.definition?.propertyType ?? PropertyType.APARTMENT;
  });

  readonly effectivePropertyId = computed<PropertyId | null>(() => {
    return this.propertyId() ?? (this.property()?._id as PropertyId) ?? null;
  });

  readonly dvfData = computed<GouvDvfDataDto | null>(() => {
    return this.propertyDvf() ?? this.property()?.geospatial?.dvf ?? this.fetchedDvfData();
  });

  readonly currentPriceM2 = computed<number | null>(() => {
    const price = this.currentPrice();
    const surface = this.effectiveSurface();
    if (!price || !surface || surface <= 0) return null;
    return Math.round(price / surface);
  });

  readonly dvfEstimatedPrice = computed<number | null>(() => {
    const dvf = this.dvfData();
    const surface = this.effectiveSurface();
    if (!dvf?.medianPriceM2 || !surface || surface <= 0) return null;
    return Math.round(dvf.medianPriceM2 * surface);
  });

  readonly dvfEstimatedPriceMin = computed<number | null>(() => {
    const price = this.dvfEstimatedPrice();
    return price ? Math.round(price * 0.9) : null;
  });

  readonly dvfEstimatedPriceMax = computed<number | null>(() => {
    const price = this.dvfEstimatedPrice();
    return price ? Math.round(price * 1.1) : null;
  });

  constructor() {
    effect(() => {
      if (this.propertyDvf() || this.property()?.geospatial?.dvf || this.fetchedDvfData()) {
        return;
      }

      const loc = this.effectiveLocation();
      const codeInsee = loc?.address?.cityCode;
      const type = this.effectivePropertyType();
      const lat = loc?.coordinates?.latitude;
      const lon = loc?.coordinates?.longitude;

      if (!codeInsee) {
        return;
      }

      const key = `${codeInsee}_${type}_${lat}_${lon}`;
      if (this.lastFetchedDvfKey === key) {
        return;
      }
      this.lastFetchedDvfKey = key;

      untracked(() => {
        this.fetchDvfData(codeInsee, type, lat, lon);
      });
    });
  }

  private fetchDvfData(codeInsee: string, type: string, lat?: number, lon?: number): void {
    const propId = this.effectivePropertyId();

    this.isDvfLoading.set(true);
    this.dvfError.set(null);

    this.apiService
      .business()
      .etxe()
      .public()
      .gouv()
      .dvf(codeInsee, type, lat, lon)
      .pipe(
        catchError((err) => {
          this.dvfError.set(err?.message || 'Erreur lors du chargement des données DVF');
          return of(null);
        }),
        tap((res: GouvDvfDataDto | null) => {
          if (res) {
            this.fetchedDvfData.set(res);
          }
        }),
        finalize(() => this.isDvfLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
